import axios from 'axios';
import { FinancialData, TradingSignal, CapeScore, FScore, PnFChart, PricePoint, Company } from '../types';

// Use environment variable or fallback to default
// In production, this would be set to the actual Cloudflare Workers endpoint
const CLOUDFLARE_API_URL = process.env.REACT_APP_CLOUDFLARE_API_URL || 'https://api.cape-trading-strategy.workers.dev';

// Helper function to handle API errors
const handleApiError = (error: any, endpoint: string) => {
  console.error(`Error calling Cloudflare API (${endpoint}):`, error);
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error('Response data:', error.response.data);
    console.error('Response status:', error.response.status);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('No response received from server');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('Error message:', error.message);
  }
  throw error;
};

// Companies API
const getCompanies = async (): Promise<Company[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/companies`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getCompanies');
  }
};

const getCompany = async (ticker: string): Promise<Company> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/companies/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getCompany(${ticker})`);
  }
};

const saveCompany = async (company: Company): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/companies`, company);
  } catch (error) {
    handleApiError(error, 'saveCompany');
  }
};

// Price History API
const getPriceHistory = async (ticker: string): Promise<PricePoint[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/price_history/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getPriceHistory(${ticker})`);
  }
};

const savePriceHistory = async (ticker: string, priceHistory: PricePoint[]): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/price_history`, { ticker, priceHistory });
  } catch (error) {
    handleApiError(error, 'savePriceHistory');
  }
};

// CAPE Scores API
const getCapeScore = async (ticker: string): Promise<CapeScore> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/cape_scores/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getCapeScore(${ticker})`);
  }
};

const getLatestCapeScores = async (): Promise<CapeScore[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/cape_scores/latest`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getLatestCapeScores');
  }
};

const saveCapeScore = async (capeScore: CapeScore): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/cape_scores`, capeScore);
  } catch (error) {
    handleApiError(error, 'saveCapeScore');
  }
};

// F-Scores API
const getFScore = async (ticker: string): Promise<FScore> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/f_scores/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getFScore(${ticker})`);
  }
};

const getLatestFScores = async (): Promise<FScore[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/f_scores/latest`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getLatestFScores');
  }
};

const saveFScore = async (fScore: FScore): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/f_scores`, fScore);
  } catch (error) {
    handleApiError(error, 'saveFScore');
  }
};

// P&F Charts API
const getPnFChart = async (ticker: string): Promise<PnFChart> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/pnf_charts/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getPnFChart(${ticker})`);
  }
};

const getLatestPnFCharts = async (): Promise<PnFChart[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/pnf_charts/latest`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getLatestPnFCharts');
  }
};

const savePnFChart = async (pnfChart: PnFChart): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/pnf_charts`, pnfChart);
  } catch (error) {
    handleApiError(error, 'savePnFChart');
  }
};

// Trading Signals API
const getTradingSignals = async (): Promise<TradingSignal[]> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/trading_signals`);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'getTradingSignals');
  }
};

const getTradingSignalsByTicker = async (ticker: string): Promise<TradingSignal> => {
  try {
    const response = await axios.get(`${CLOUDFLARE_API_URL}/trading_signals/${ticker}`);
    return response.data;
  } catch (error) {
    return handleApiError(error, `getTradingSignalsByTicker(${ticker})`);
  }
};

const saveTradingSignals = async (signals: TradingSignal[]): Promise<void> => {
  try {
    await axios.post(`${CLOUDFLARE_API_URL}/trading_signals`, signals);
  } catch (error) {
    handleApiError(error, 'saveTradingSignals');
  }
};

// Financial Data Search API
const searchFinancialData = async (ticker: string, query?: string) => {
  try {
    const response = await axios.post(`${CLOUDFLARE_API_URL}/financial-data/search`, { ticker, query });
    return response.data;
  } catch (error) {
    return handleApiError(error, `searchFinancialData(${ticker}, ${query})`);
  }
};

// Get all financial data for a ticker
const getFinancialData = async (ticker: string): Promise<FinancialData> => {
  try {
    // Fetch all the data in parallel
    const [priceHistory] = await Promise.all([
      getPriceHistory(ticker),
      getCapeScore(ticker).catch(() => null), // We still want to execute this but don't need the result
      getFScore(ticker).catch(() => null),    // We still want to execute this but don't need the result
      getPnFChart(ticker).catch(() => null)   // We still want to execute this but don't need the result
    ]);
    
    // Get income statement, balance sheet and cash flow data
    // In a real implementation, these would be fetched from Alpha Vantage or Cloudflare cache
    // For now, we'll return empty arrays
    
    return {
      ticker,
      priceHistory,
      incomeStatement: [], // This would come from Alpha Vantage or Cloudflare cache
      balanceSheet: [],    // This would come from Alpha Vantage or Cloudflare cache
      cashFlow: [],        // This would come from Alpha Vantage or Cloudflare cache
      lastUpdated: Date.now()
    };
  } catch (error) {
    return handleApiError(error, `getFinancialData(${ticker})`);
  }
};

// Export the service
const cloudflareService = {
  // Companies
  getCompanies,
  getCompany,
  saveCompany,
  
  // Price History
  getPriceHistory,
  savePriceHistory,
  
  // CAPE Scores
  getCapeScore,
  getLatestCapeScores,
  saveCapeScore,
  
  // F-Scores
  getFScore,
  getLatestFScores,
  saveFScore,
  
  // P&F Charts
  getPnFChart,
  getLatestPnFCharts,
  savePnFChart,
  
  // Trading Signals
  getTradingSignals,
  getTradingSignalsByTicker,
  saveTradingSignals,
  
  // Financial Data Search
  searchFinancialData,
  getFinancialData
};

export default cloudflareService; 